/* agb-atc-added.js - the Add to cart button says so once it has.
 *
 * Dorian 2026-09-16: "When clicking add to cart it should then be written in
 * lighter green added". His own PDP demo (Downloads/index.html, add()) is the
 * reference: the label swaps, an `.added` class colours the button, and it all
 * comes back 1.5 s later. Same numbers here.
 *
 * WHAT IT WAITS FOR - AND WHY NOT THE CLICK. The theme's product form posts to
 * /cart/add.js and, ONLY when the response is ok, dispatches `variant:add` on
 * the form (theme.js, onSubmit_fn). That is the signal. A click is not one:
 * the US body-armour terms gate (terms-gate-standard.js) stops the click in
 * the capture phase when the box is unchecked, a sold-out or capped line comes
 * back 422, and a cart_type of "page" leaves for /cart before anything is
 * dispatched. In every one of those cases nothing was added, and a button
 * that turned green on the click would have said otherwise.
 *
 * WHICH BUTTONS. The same set the theme itself disables while the request is
 * out: `form.elements` filtered to type=submit - so a button that sits
 * outside the form but points at it with form="..." is included, and the
 * dynamic payment button (not a submit control) is not.
 *
 * THE WORD. `product.general.added_button` ("Added"), a key added to every
 * locale file with its own word per language, handed over by theme.liquid as
 * window.AGB_ATC_ADDED_LABEL. The theme's longer sentence
 * (`added_to_cart`, on window.themeVariables.strings) is only the fallback.
 * Nothing is hardcoded; a locale without the key falls back to the default
 * locale the way every Shopify string does.
 *
 * THE COLOUR. His olive #92AE74: the green his demo uses for the button, the
 * `--agmf-accent` the media feed's own buttons already wear on this page, and
 * lighter than the demo's `.added` #687742. Written through the theme's own
 * `--button-background` AND as a direct background-color: on a feed page the
 * media flow paints every button dark green directly (html.agmf-feed-live
 * .button) and never reads the variable, and on the others the button carries
 * the variable INLINE from the section settings - `!important` beats both.
 *
 * COMES BACK EARLY when the shopper changes variant (the line in the cart is
 * no longer the one on screen - `variant:change` bubbles from the same form),
 * when the form submits again (the theme's own busy state must show the real
 * label), and on a bfcache restore (a timer does not survive one). The saved
 * label is written back only while the button still shows ours: on a variant
 * change the theme has already put the right words there for the new variant.
 *
 * THE GREEN ARRIVES AS A BAR, NOT AS A FLASH (Dorian 2026-09-29: "the light
 * green color is filling progressively (like load bar) from left to right
 * instead of suddently appear on the button. But it should take exactly same
 * timing as now").
 *
 * NOTHING ABOUT THE TIMING MOVED, and that was the constraint. Measured on the
 * preview before writing it: submit -> variant:add is 1036 ms on this page and
 * variant:add -> the drawer opening is another 15. The bar fills the interval
 * that was already there and empty. It starts on the submit, where the label
 * was already changing; "Added" still lands on variant:add and still holds for
 * HOLD_MS. The shopper waits exactly as long as before - the wait is simply
 * shown.
 *
 * WHICH IS WHY IT CANNOT BE A FIXED ANIMATION. That interval belongs to the
 * network, not to us: a second on a warm connection, several on a cold one. A
 * bar with a duration would either finish early and sit full while nothing was
 * happening, or still be crawling when the drawer opened - and the second one
 * is a lie about a cart that already has the line in it. So it never arrives
 * on its own: it approaches FILL_CAP and stops short, then runs home when the
 * real answer arrives.
 *
 * ONE PACE, NOT A SLOWING CURVE (Dorian 2026-10-07: "right now it is smooth up
 * to about 70% then it kind of get slower"). The first version was a pure
 * exponential, and an exponential slows from its first frame: at 70% it was
 * already moving at a quarter of its starting speed, which is exactly the
 * stall he saw. Now the bar moves at ONE speed to KNEE, chosen so it would
 * reach KNEE at KNEE_AT of the add time this device paces for; only past that
 * does the same speed bleed off towards FILL_CAP, with no kink where it
 * starts. That time is LEARNED - the slower end of this device's last few
 * submit -> variant:add times (a phone on 4G is not a desk on fibre), starting
 * from the 1.04 s measured on the preview - so nearly every add answers while
 * the bar is still at full speed, the run home carries on at an even 1.5x,
 * and the slow tail is only ever seen by a request that really is late.
 *
 * WHY box-shadow AND NOT THE BACKGROUND. The two ways this button gets painted
 * are described above, and neither can carry a bar. On a feed page the fill is
 * a background-COLOR, which has no left and no right. Everywhere else it is a
 * two-layer background-IMAGE whose per-layer background-size list the theme
 * animates on hover (`background-size .45s`, measured) - inserting a layer
 * shifts that list under the theme's own rule and the hover sweep starts
 * painting the wrong layer. An inset box-shadow offset sideways draws a band
 * over whatever is underneath, in one value rather than a per-layer list; it
 * sits above the background and below the label; it is "none" on this button
 * at rest on both page types, so there is nothing to capture and nothing to
 * put back; and it is the one paintable property the theme's `transition`
 * does not name, so each frame lands instantly instead of chasing a 0.45s
 * ease of its own.
 *
 * Isolation: own file, reads a global and two DOM events, edits nothing the
 * theme owns. Kill: window.AGB_ATC_ADDED_OFF = true before it loads.
 */
(function () {
  'use strict';
  if (window.AGB_ATC_ADDED_OFF) return;

  var HOLD_MS = 1500;
  var PENDING_CAP_MS = 8000;          // a request that never answers must not leave a label behind
  var CLASS = 'agb-atc-added';
  var FILLING = 'agb-atc-filling';    // marker only; the bar is an inline style
  var GREEN = '146 174 116';

  var PACE_DEFAULT_MS = 1050;         // submit -> variant:add, measured 1036 ms on the preview
  var PACE_MIN_MS = 500;
  var PACE_MAX_MS = 3000;
  var PACE_KEY = 'agb_atc_ms';        // this device's last few add times
  var PACE_KEEP = 5;
  var KNEE = 80;                      // one speed up to here...
  var KNEE_AT = 0.95;                 // ...reached just before the add time paced for
  var FILL_CAP = 98;                  // ...then that speed bleeds off towards this; never reached
  var HOME_PACE = 1.5;                // the run home once the cart has answered: an even
  var HOME_MIN_MS = 120;              // 1.5x the fill pace, so the finish does not lurch
  var HOME_MAX_MS = 420;

  var REDUCED = false;
  try {
    REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  // Two ways a button gets its fill here, both covered. The theme paints
  // `.button` through a gradient built from --button-background (the section
  // settings write that variable inline, hence !important on the variable);
  // the media feed then paints every PDP button DIRECTLY - `html.agmf-feed-live
  // .button{background:rgb(36 64 52)}`, the dark green the shopper sees at rest -
  // which never reads the variable at all - and it is !important at specificity
  // (0,2,1), so a plain `.button.agb-atc-added` (0,2,0) loses to it even with
  // !important of its own. The second selector names that rule's own prefix
  // and outranks it at (0,3,1); the first covers every other template.
  var style = document.createElement('style');
  style.textContent =
    '.button.' + CLASS + ',html.agmf-feed-live .button.' + CLASS + '{' +
      '--button-background:' + GREEN + ' !important;' +
      '--button-outline-color:' + GREEN + ' !important;' +
      'background-color:rgb(' + GREEN + ') !important;' +
      'background-image:none !important;' +
      'border-color:rgb(' + GREEN + ') !important;' +
    '}';
  (document.head || document.documentElement).appendChild(style);

  // button -> { html, timer }; a WeakMap so a re-rendered button takes nothing with it
  var held = new WeakMap();

  /* ---- the bar ------------------------------------------------------------
     The frame loop only PAINTS. Every state change - the run home, the
     handover to the flat colour - is driven by a timer instead, because a
     backgrounded tab freezes requestAnimationFrame: a shopper who clicks and
     switches tab must not come back to a button left half green. */
  function paintFill(button, h) {
    // sub-pixel on purpose: rounding to whole pixels made the edge step
    var w = (h.width * Math.min(100, Math.max(0, h.pct)) / 100).toFixed(2);
    button.style.setProperty('box-shadow',
      'inset ' + (h.rtl ? '-' + w : w) + 'px 0 0 0 rgb(' + GREEN + ')', 'important');
  }

  // The add time to pace for on THIS device: the slower end of the last few
  // (their 75th percentile), not the middle. Paced for the median, half of all
  // adds answered after the knee and spent their last moment crawling -
  // measured on the preview: learned 981 ms, answered at 1144, +1.3% in the
  // last 100 ms. Paced a little slow, the answer nearly always lands while the
  // bar is at full speed, and the run home covers the difference. Not the
  // maximum, so one stalled request cannot slow the next five bars.
  function expectedMs() {
    var list = [];
    try { list = JSON.parse(localStorage.getItem(PACE_KEY) || '[]') || []; } catch (e) { list = []; }
    list = list.filter(function (n) { return typeof n === 'number' && n > 0; });
    if (!list.length) return PACE_DEFAULT_MS;
    var s = list.slice().sort(function (a, b) { return a - b; });
    var p75 = s[Math.max(0, Math.ceil(0.75 * s.length) - 1)];
    return Math.min(PACE_MAX_MS, Math.max(PACE_MIN_MS, p75));
  }
  function learnPace(ms) {
    // a hidden tab stretches the interval without the network being slow
    if (!(ms > 150 && ms < 8000) || document.visibilityState !== 'visible') return;
    try {
      var list = JSON.parse(localStorage.getItem(PACE_KEY) || '[]') || [];
      list.push(Math.round(ms));
      localStorage.setItem(PACE_KEY, JSON.stringify(list.slice(-PACE_KEEP)));
    } catch (e) {}
  }
  // Straight line at speed v to KNEE; past it the SAME speed decays towards
  // FILL_CAP (its slope at the knee is exactly v, so there is no visible kink).
  function paceAt(t, v) {
    var tKnee = KNEE / v;
    if (t <= tKnee) return v * t;
    var room = FILL_CAP - KNEE;
    return KNEE + room * (1 - Math.exp(-v * (t - tKnee) / room));
  }

  function startFill(button, h) {
    if (REDUCED) return;
    /* Measured once, not per frame. The loop writes a style every frame, so
       reading a width back inside it would force a synchronous layout for a
       number that cannot change while one cart request is out. Hebrew and
       Arabic fill from THEIR start edge: "left to right" is the direction the
       page reads in, and a negative offset puts the band on the right. */
    try { h.rtl = getComputedStyle(button).direction === 'rtl'; } catch (e) { h.rtl = false; }
    h.width = button.clientWidth;
    if (!h.width) return;
    h.pct = 0;
    h.v = KNEE / (KNEE_AT * expectedMs());     // percent per millisecond
    h.sentAt = performance.now();
    h.filling = true;
    button.classList.add(FILLING);

    function step(ts) {
      h.raf = 0;
      if (held.get(button) !== h || !h.filling) return;
      if (!h.t0) h.t0 = ts;
      if (h.homeAt) {
        // an even pace to the end: the far edge is the button's own edge, so
        // arriving at speed is invisible, and an ease would lurch at the start
        var u = Math.min(1, Math.max(0, (ts - h.homeAt) / h.homeMs));
        h.pct = h.homeFrom + (100 - h.homeFrom) * u;
      } else {
        h.pct = paceAt(ts - h.t0, h.v);
      }
      paintFill(button, h);
      if (h.pct >= 100) return;         // full: the flat colour takes it from here
      h.raf = requestAnimationFrame(step);
    }
    h.raf = requestAnimationFrame(step);
  }

  // the cart has answered: stop approaching and arrive. Returns how long the
  // run home takes, or 0 when there is no bar at all - none under
  // prefers-reduced-motion, and none when variant:add arrives without a
  // submit of ours having started one.
  function fillHome(h) {
    if (!h || !h.filling) return 0;
    learnPace(performance.now() - h.sentAt);
    h.homeFrom = h.pct;
    // what is left, a little faster than it was filling
    h.homeMs = Math.round(Math.min(HOME_MAX_MS,
      Math.max(HOME_MIN_MS, (100 - h.pct) / (HOME_PACE * h.v))));
    h.homeAt = performance.now();       // the same clock requestAnimationFrame stamps with
    return h.homeMs;
  }

  function clearFill(button, h) {
    if (!h || !h.filling) return;
    h.filling = false;
    if (h.raf) cancelAnimationFrame(h.raf);
    h.raf = 0;
    button.style.removeProperty('box-shadow');   // and nothing else inline: the
    button.classList.remove(FILLING);            // section writes its own vars here
  }

  // The word first (product.general.added_button, handed over by theme.liquid
  // as AGB_ATC_ADDED_LABEL - Dorian 2026-09-16: "write 'added' not the full
  // sentence"); the theme's own sentence only if that is somehow missing.
  function addingLabel() {
    try {
      var w = window.AGB_ATC_ADDING_LABEL;
      return (typeof w === 'string' && w.trim()) ? w.trim() : '';
    } catch (e) { return ''; }
  }

  function label() {
    try {
      var w = window.AGB_ATC_ADDED_LABEL;
      if (typeof w === 'string' && w.trim()) return w.trim();
      var s = window.themeVariables && window.themeVariables.strings;
      return (s && typeof s.addedToCart === 'string' && s.addedToCart) || '';
    } catch (e) { return ''; }
  }

  function submitButtonsOf(form) {
    if (!form || !form.elements) return [];
    return Array.prototype.filter.call(form.elements, function (el) {
      return el && el.type === 'submit' && el.classList && el.classList.contains('button');
    });
  }

  // The label goes back ONLY if it is still ours. On variant:change the
  // theme's <buy-buttons> has already rewritten it for the new variant -
  // "Sold out", "Pre-order", "Unavailable" or "Add to cart" (theme.js
  // onVariantChanged_fn) - and it listens on the FORM, so it runs before this
  // document-level listener. Writing the saved label over that would put
  // "Add to cart" back on a variant that just went sold out.
  function restore(button) {
    var h = held.get(button);
    if (!h) return;
    clearTimeout(h.timer);
    clearFill(button, h);
    held.delete(button);
    if (button.textContent.trim() === h.shown) button.innerHTML = h.html;
    button.classList.remove(CLASS);
    button.removeAttribute('aria-live');
  }

  /* ---- THE BUTTON SAYS IT IS WORKING, THEN THAT IT IS DONE ---------------
     (Dorian 2026-09-16: "by the second we click add to cart it should be
     written adding to cart until the cart opens / like it does it on all non
     magnetix pd pages".)

     Measured before writing this. On a plain page the button only goes
     disabled with aria-busy and says nothing for the ~1.2s the request takes;
     the pages that DO speak are the gear-builder ones, whose own
     `#add-all-btn` writes 'Adding...' (main-product-agb-system.liquid:1884).
     That button is a type="button" outside any product form, so it never
     reaches the submit listener below and cannot end up labelled twice.

     One entry per button, two modes. `pending` is set on the form's submit -
     the event, not the click, because the US body-armour terms gate stops a
     blocked click in the capture phase and a label keyed off the click would
     announce an add that is not happening. `added` replaces it when the
     theme's variant:add says the line is really in the cart. */
  function pending(button) {
    var h = held.get(button);
    if (h && h.mode === 'added') return;      // already finished; nothing to announce
    if (h && h.mode === 'pending') return;    // a second submit inside one request
    var text = addingLabel();
    h = { html: button.innerHTML, shown: text || button.textContent.trim(),
          mode: 'pending', timer: 0 };
    held.set(button, h);
    if (text) {
      button.setAttribute('aria-live', 'polite');
      button.textContent = text;
    }
    startFill(button, h);
    // never a permanent label: if nothing ever answers, put it back
    h.timer = setTimeout(function () { restore(button); }, PENDING_CAP_MS);
  }

  function paint(button) {
    var h = held.get(button);
    if (h && h.mode === 'added') {
      // a second add while the first is still showing: restart the clock
      clearTimeout(h.timer);
      h.timer = setTimeout(function () { restore(button); }, HOLD_MS);
      return;
    }
    var text = label();
    var home = 0;
    if (h && h.mode === 'pending') {
      // keep the ORIGINAL label captured before "Adding" replaced it
      clearTimeout(h.timer);
      h.mode = 'added';
      h.shown = text || button.textContent.trim();
      home = fillHome(h);
    } else {
      h = { html: button.innerHTML, shown: text || button.textContent.trim(),
            mode: 'added', timer: 0 };
      held.set(button, h);
    }
    if (home) {
      /* The class is the FLAT olive, which is what a bar at 100% already
         looks like - so the swap at the end of the run home is not a frame
         anybody can see, and the state the hold and the restore deal with is
         the same one they have always dealt with. The guard is for a restore
         that lands inside the run home: variant:change puts the right words
         on the button for the new variant, and this must not paint over it. */
      var settled = h;
      setTimeout(function () {
        if (held.get(button) !== settled || settled.mode !== 'added') return;
        button.classList.add(CLASS);
        clearFill(button, settled);
      }, home);
    } else {
      button.classList.add(CLASS);
    }
    if (text) {
      button.setAttribute('aria-live', 'polite');
      button.textContent = text;
    }
    h.timer = setTimeout(function () { restore(button); }, HOLD_MS);
  }

  function endPending(button) {
    var h = held.get(button);
    if (h && h.mode === 'pending') restore(button);
  }

  function restoreAll(form) {
    submitButtonsOf(form).forEach(restore);
  }

  document.addEventListener('variant:add', function (e) {
    var form = e && e.target;
    if (!form || !form.elements) return;
    submitButtonsOf(form).forEach(paint);
  });

  document.addEventListener('variant:change', function (e) {
    if (e && e.target && e.target.elements) restoreAll(e.target);
  });

  /* The submit listener is on document in the BUBBLE phase on purpose: the
     terms gate and the free-gift row both stop the event in the capture phase
     when they take over, so an add they cancelled or drive themselves never
     reaches here and never paints a label that would have to be taken back. */
  /* ...AND ONLY ON A FORM THAT ADDS TO THE CART (Dorian 2026-10-08, with a
     screenshot of the search overlay: "when we lauch a full resarch its making
     the same button as adding to cart with adding"). The listener is on
     document, so EVERY form on the page reached it, and the only test was "a
     submit button carrying the class `button`". Measured on live: the header's
     predictive search is action="/search", and the moment results arrive it
     grows exactly such a button - "View all results" - so running a search
     painted `Adding` over it. Any other form built the same way did too.

     `{% form 'product' %}` always renders action="/cart/add" (with the locale
     root where there is one: /en/cart/add on IL), and the three hand-written
     AGB product forms use routes.cart_add_url, which is the same path - so one
     substring keeps every real add and excludes every form that is not one.
     Verified on live before narrowing: the product form reports action
     "/cart/add" with both "Add to cart" buttons still matching. */
  function addsToCart(form) {
    var a = (form && form.getAttribute && form.getAttribute('action')) || '';
    return a.indexOf('/cart/add') !== -1;
  }
  document.addEventListener('submit', function (e) {
    if (e && e.target && e.target.elements && addsToCart(e.target)) {
      submitButtonsOf(e.target).forEach(pending);
    }
  });

  /* "UNTIL THE CART OPENS" - taken literally. variant:add lands about ten
     milliseconds before the drawer opens, so in the ordinary case `paint`
     above has already turned the label into "Added" and this does nothing.
     It is here for the paths that open the cart WITHOUT dispatching
     variant:add - the free-gift row posts its own /cart/add.js - where it is
     the only thing that ends the pending label at the right moment. The
     drawer's `open` ATTRIBUTE is the signal rather than an event: measured on
     this theme during the analytics work, the add-to-cart path leaves the
     drawer open without ever dispatching dialog:after-show. */
  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.agbAtcWatched) return;
    drawer.agbAtcWatched = true;
    new MutationObserver(function () {
      if (!drawer.hasAttribute('open')) return;
      Array.prototype.forEach.call(
        document.querySelectorAll('form button[type="submit"], form [type="submit"].button'),
        endPending);
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  } else {
    watchDrawer();
  }
  document.addEventListener('cart:error', function () {
    Array.prototype.forEach.call(
      document.querySelectorAll('form button[type="submit"], form [type="submit"].button'),
      endPending);
  });

  /* ---- THE GEAR BUILDER'S OWN BUTTON ------------------------------------
     (Dorian 2026-10-07, with a screenshot of a plain "ADD TO CART" on a new
     gear-builder page: "all the new pages we make should have the same as
     magnetix, e.g. the add to cart load animation".)

     #add-all-btn / #add-all-btn-2 (main-product-agb-system, -helmet-cover,
     -test-realtest) are type="button" and post their own lines, so neither the
     submit nor variant:add above ever sees them - those pages had no bar and
     said "Adding..." / "Added!" in English whatever the page's language. They
     DO announce themselves, though: the section's handler writes 'Adding...'
     the moment its request starts (after its own colour/size and terms checks,
     so a refused click never gets here), 'Added!' when the cart holds the
     lines, and its saved label back on a failure or after its own hold.

     So the handler's words are the signal - READ, never driven: nothing in
     the section is edited or called. The moment it says 'Adding...' this
     button gets the same bar and the same translated word as every other Add
     to cart; 'Added!' runs it home and shows "Added"; any other words mean it
     is over. The resting label is remembered from before the request, because
     by the time 'Adding...' is observed it has already been overwritten. */
  var BUILDER_BUTTONS = '#add-all-btn, #add-all-btn-2';
  var BUILDER_ADDING = /^adding\.\.\.$/i;   // the handler's own words, exactly
  var BUILDER_ADDED = /^added!$/i;

  function watchBuilderButton(btn) {
    if (!btn || btn.agbAtcBuilder) return;
    btn.agbAtcBuilder = true;
    var rest = btn.innerHTML;                  // its label while nothing is happening
    new MutationObserver(function () {
      var t = btn.textContent.trim();
      var h = held.get(btn);
      if (BUILDER_ADDING.test(t)) {
        if (h) return;
        pending(btn);
        h = held.get(btn);
        if (h) h.html = rest;                  // what to put back is the resting label
        return;
      }
      if (BUILDER_ADDED.test(t)) {
        if (!h || h.mode === 'pending') paint(btn);
        h = held.get(btn);
        if (h) h.html = rest;
        return;
      }
      // Our own words are not news. Anything else is the handler putting its
      // label back (a failed add, or the end of its hold): it is over.
      if (h && (t === h.shown || t === addingLabel() || t === label())) return;
      if (h) restore(btn);
      rest = btn.innerHTML;
    }).observe(btn, { childList: true, characterData: true, subtree: true });
  }
  function watchBuilderButtons() {
    Array.prototype.forEach.call(document.querySelectorAll(BUILDER_BUTTONS), watchBuilderButton);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchBuilderButtons);
  } else {
    watchBuilderButtons();
  }

  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    Array.prototype.forEach.call(
      document.querySelectorAll('.' + CLASS + ',.' + FILLING), restore);
  });
})();
